import React, {ErrorInfo, useState} from 'react';
import {StyleSheet, Text, View} from 'react-native';
import Alert from "@/lib/Alert";
import {ErrorBoundary, FallbackProps} from 'react-error-boundary';
import {router} from 'expo-router';
import {useTranslation} from 'react-i18next';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {useLogout} from "@/auth/useLogout";
import {ThemeType} from "@/theming/baseTheme";
import {ClientResponseError} from "pocketbase";
import axios from "axios";
import * as Sentry from "@sentry/react-native";

// Button component for the error screen
interface ErrorButtonProps {
    title: string;
    onPress: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
    theme: ThemeType;
}

const ErrorButton: React.FC<ErrorButtonProps> = ({title, onPress, variant = 'primary', theme}) => {
    const styles = createButtonStyles(theme);

    const buttonStyle = [
        styles.button,
        variant === 'primary' && styles.primaryButton,
        variant === 'secondary' && styles.secondaryButton,
        variant === 'danger' && styles.dangerButton,
    ];

    const textStyle = [
        styles.buttonText,
        variant === 'primary' && styles.primaryButtonText,
        variant === 'secondary' && styles.secondaryButtonText,
        variant === 'danger' && styles.dangerButtonText,
    ];

    return (
        <View style={buttonStyle}>
            <Text style={textStyle} onPress={onPress}>
                {title}
            </Text>
        </View>
    );
};

// Error fallback component
interface ErrorFallbackProps extends FallbackProps {
    maxRetries?: number;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
                                                         error,
                                                         resetErrorBoundary,
                                                         maxRetries = 3
                                                     }) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const {logout} = useLogout();
    const [retryCount, setRetryCount] = useState(0);
    const styles = createStyles(theme);

    const isNetworkError = error.message?.toLowerCase().includes('network') ||
        error.message?.toLowerCase().includes('fetch');

    // Check for authentication errors in multiple ways:
    // 1. Custom isAuthError flag from API interceptor
    // 2. PocketBase ClientResponseError with 401/403 status
    // 3. String patterns in error message
    const isAuthError =
        (error as any).isAuthError === true ||
        (error instanceof ClientResponseError && (error.status === 401 || error.status === 403)) ||
        error.message?.toLowerCase().includes('401') ||
        error.message?.toLowerCase().includes('403') ||
        error.message?.toLowerCase().includes('unauthorized') ||
        error.message?.toLowerCase().includes('forbidden') ||
        error.message?.toLowerCase().includes('authentication failed') ||
        error.message?.toLowerCase().includes('session has expired');

    const canRetry = retryCount < maxRetries;

    const handleRetry = () => {
        if (retryCount >= maxRetries) {
            Alert.alert(
                t('errors.types.generic'),
                t('errors.messages.genericError'),
                [{text: 'OK'}]
            );
            return;
        }

        setRetryCount(prev => prev + 1);
        resetErrorBoundary();
    };

    const handleLogout = async () => {
        try {
            await logout();
            router.replace('/');
        } catch (logoutError) {
            console.error('Logout failed:', logoutError);
            Alert.alert(t('common.error'), t('auth.logout.error'));
        }
    };

    const handleAdvancedActions = () => {
        Alert.alert(
            t('errors.actions.moreOptions'),
            t('errors.actions.chooseAction'),
            [
                {text: t('errors.actions.refreshApp'), onPress: () => router.replace('/')},
                {text: t('errors.actions.debugData'), onPress: () => router.replace('/data')},
                {
                    text: t('errors.actions.clearCacheRetry'), onPress: () => {
                        // You can add cache clearing logic here
                        handleRetry();
                    }
                },
                {text: t('common.cancel'), style: 'cancel'},
            ]
        );
    };

    return (
        <View style={styles.container}>
            <View style={styles.content}>
                {/* Error Icon */}
                <View style={styles.iconContainer}>
                    <Text style={styles.errorIcon}>⚠️</Text>
                </View>

                {/* Error Title */}
                <Text style={styles.title}>
                    {isAuthError ? t('errors.types.authentication') :
                        isNetworkError ? t('errors.types.network') :
                            t('errors.types.generic')}
                </Text>

                {/* Error Description */}
                <Text style={styles.description}>
                    {isAuthError ?
                        t('errors.messages.sessionExpired') :
                        isNetworkError ?
                            t('errors.messages.networkError') :
                            t('errors.messages.genericError')}
                </Text>

                {/* Technical Details */}
                <View style={styles.technicalDetails}>
                    <Text style={styles.technicalTitle}>{t('errors.technical.title')}</Text>
                    <Text style={styles.technicalText}>{error.message}</Text>
                    {retryCount > 0 && (
                        <Text style={styles.retryText}>
                            {t('errors.technical.retryAttempts', { current: retryCount, max: maxRetries })}
                        </Text>
                    )}
                </View>

                {/* Action Buttons */}
                <View style={styles.buttonContainer}>
                    {canRetry && !isAuthError && (
                        <ErrorButton
                            title={t('errors.actions.tryAgain')}
                            onPress={handleRetry}
                            variant="primary"
                            theme={theme}
                        />
                    )}

                    {isAuthError ? (
                        <ErrorButton
                            title={t('errors.actions.loginAgain')}
                            onPress={handleLogout}
                            variant="primary"
                            theme={theme}
                        />
                    ) : (
                        <ErrorButton
                            title={t('errors.actions.logout')}
                            onPress={handleLogout}
                            variant="danger"
                            theme={theme}
                        />
                    )}

                    <ErrorButton
                        title={t('errors.actions.moreOptions')}
                        onPress={handleAdvancedActions}
                        variant="secondary"
                        theme={theme}
                    />
                </View>
            </View>
        </View>
    );
};

// Main Error Boundary Wrapper
interface ErrorBoundaryWrapperProps {
    children: React.ReactNode;
    maxRetries?: number;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    fallback?: React.ComponentType<ErrorFallbackProps>;
}

export const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({
                                                                      children,
                                                                      maxRetries = 3,
                                                                      onError,
                                                                      fallback: CustomFallback
                                                                  }) => {
    const handleError = (error: Error, errorInfo: ErrorInfo) => {
        // Ignore axios cancellation errors - these are intentional (e.g., when user is logged out)
        if (axios.isCancel(error)) {
            console.log('Request cancelled (user logged out):', error.message);
            return;
        }

        // Log error for debugging
        console.error('Error Boundary caught an error:', error, errorInfo);

        // Call custom error handler if provided
        if (onError) {
            onError(error, errorInfo);
        }

        Sentry.captureException(error);
    };

    const FallbackComponent = CustomFallback || ErrorFallback;

    return (
        <ErrorBoundary
            FallbackComponent={(props) => (
                <FallbackComponent {...props} maxRetries={maxRetries}/>
            )}
            onError={handleError}
        >
            {children}
        </ErrorBoundary>
    );
};

// Custom hook for programmatic error throwing (useful for testing)
export const useThrowError = () => {
    const throwError = (error: string | Error) => {
        throw typeof error === 'string' ? new Error(error) : error;
    };

    return {throwError};
};

// Custom hook for resetting error boundary from within components
export const useErrorHandler = () => {
    const [error, setError] = useState<Error | null>(null);

    const resetError = () => setError(null);

    const handleError = (error: Error) => {
        setError(error);
    };

    // Re-throw error to trigger error boundary
    if (error) {
        throw error;
    }

    return {handleError, resetError};
};

// Styles
const createStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing.lg,
    },
    content: {
        alignItems: 'center',
        maxWidth: 400,
        width: '100%',
    },
    iconContainer: {
        marginBottom: theme.spacing.lg,
    },
    errorIcon: {
        fontSize: 64,
        textAlign: 'center',
    },
    title: {
        fontSize: theme.fontSize.xl,
        fontWeight: 'bold',
        color: theme.colors.text,
        textAlign: 'center',
        marginBottom: theme.spacing.md,
    },
    description: {
        fontSize: theme.fontSize.md,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        lineHeight: 24,
        marginBottom: theme.spacing.lg,
    },
    technicalDetails: {
        backgroundColor: theme.colors.surface,
        padding: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
        marginBottom: theme.spacing.xl,
        width: '100%',
    },
    technicalTitle: {
        fontSize: theme.fontSize.sm,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: theme.spacing.xs,
    },
    technicalText: {
        fontSize: theme.fontSize.xs,
        color: theme.colors.textSecondary,
        fontFamily: 'monospace',
    },
    retryText: {
        fontSize: theme.fontSize.xs,
        color: theme.colors.warning,
        marginTop: theme.spacing.xs,
    },
    buttonContainer: {
        width: '100%',
        gap: theme.spacing.md,
    },
});

const createButtonStyles = (theme: ThemeType) => StyleSheet.create({
    button: {
        paddingVertical: theme.spacing.md,
        paddingHorizontal: theme.spacing.lg,
        borderRadius: theme.borderRadius.md,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 48,
    },
    primaryButton: {
        backgroundColor: theme.colors.primary,
    },
    secondaryButton: {
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    dangerButton: {
        backgroundColor: theme.colors.error,
    },
    buttonText: {
        fontSize: theme.fontSize.md,
        fontWeight: '600',
    },
    primaryButtonText: {
        color: '#ffffff',
    },
    secondaryButtonText: {
        color: theme.colors.text,
    },
    dangerButtonText: {
        color: '#ffffff',
    },
});

// Usage Examples:
/*
// 1. Install the required dependency first:
// npm install react-error-boundary

// 2. Basic usage in your app layout:
export default function RootLayout() {
    return (
        <AppErrorBoundary
            maxRetries={3}
            onError={(error, errorInfo) => {
                // Send to crash reporting
                console.error('App Error:', error);
                console.error('Component Stack:', errorInfo.componentStack);
                // crashlytics().recordError(error);
            }}
        >
            <PersistQueryClientProvider client={queryClient}>
                <Suspense fallback={<AppLoadingScreen />}>
                    <MainApp />
                </Suspense>
            </PersistQueryClientProvider>
        </AppErrorBoundary>
    );
}

// 3. With custom error screen:
const CustomErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetErrorBoundary }) => {
    const { logout } = useLogout();

    return (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <Text>Custom Error: {error.message}</Text>
            <Button title="Retry" onPress={resetErrorBoundary} />
            <Button title="Logout" onPress={logout} />
        </View>
    );
};

<AppErrorBoundary fallback={CustomErrorFallback}>
    <YourApp />
</AppErrorBoundary>

// 4. Specific screen error boundaries:
<AppErrorBoundary maxRetries={2}>
    <OrganizationScreen orgId={orgId} />
</AppErrorBoundary>

// 5. Programmatically trigger errors (useful for testing):
const TestComponent = () => {
    const { throwError } = useThrowError();
    const { handleError } = useErrorHandler();

    return (
        <Button
            title="Test Error"
            onPress={() => throwError('Test error message')}
        />
    );
};
*/
